import { ConfirmationNumberOutlined, SearchOff } from '@mui/icons-material';
import { Box, Stack, ThemeProvider } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';

import TicketForm from '../components/ticketForm';
import TicketTable from '../components/ticketTable';
import TicketToolbar from '../components/ticketToolbar';
import ViewTicket from '../components/viewTicket';
import ConfirmationDialog from 'components/v2/common/confirmationDialog';
import DocumentPreview from 'components/v2/common/documentPreview';
import Result from 'components/v2/common/Result';

import api from 'api';
import { ChatReference } from 'api/rest/chat_new';
import { ACTIONS } from 'constant';
import { TicketStatus } from 'data/constants/constants';
import useModalObj from 'hooks/useModalObj';
import useTranslate from 'hooks/useTranslate';
import { setAlert } from 'redux/alert';
import { loadDocs } from 'redux/application/documents/documents-middleware';
import { AppDispatch, RootState } from 'redux/store';
import appThemeV2 from 'theme/themeV2';
import { TicketInterface } from 'types';
import { getErrorMessage } from 'utils/v2/errorHelper';
import { ticketInitialValues } from 'utils/validations/ticket';

const Tickets = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const { translate } = useTranslate();
    const { entity: activeApp } = useSelector((state: RootState) => state.application);
    const { list: docsList } = useSelector((state: RootState) => state.documentsApp);

    const [tickets, setTickets] = useState<TicketInterface[]>([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState<TicketStatus | null>(null);
    const [previewFile, setPreviewFile] = useState<{ fileUrl: string; fileName: string } | null>(
        null
    );

    const { modalObject, onResetModalObj, setModalObject } = useModalObj<TicketInterface>();

    const fetchTickets = useCallback(async () => {
        if (!activeApp?.id) return;
        setLoading(true);
        try {
            const response = await api.tickets.getTicketsByApplication(activeApp.id.toString());
            setTickets((response.data as unknown as TicketInterface[]) ?? []);
        } catch (error) {
            dispatch(
                setAlert({
                    message: getErrorMessage(error, translate('ticketsLoadError')),
                    type: 'error',
                })
            );
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeApp?.id, dispatch]);

    useEffect(() => {
        fetchTickets();
    }, [fetchTickets]);

    useEffect(() => {
        if (activeApp?.id) dispatch(loadDocs(activeApp.id.toString()));
    }, [activeApp?.id, dispatch]);

    const searchFilteredTickets = useMemo(() => {
        if (!search.trim()) return tickets;
        const query = search.toLowerCase();
        return tickets.filter(
            (ticket) =>
                String(ticket.id).includes(query) ||
                ticket.text?.toLowerCase().includes(query) ||
                ticket.description?.toLowerCase().includes(query) ||
                ticket.document_name?.toLowerCase().includes(query)
        );
    }, [tickets, search]);

    const statusCounts = useMemo(() => {
        const map = new Map<TicketStatus, number>();
        for (const ticket of searchFilteredTickets) {
            map.set(ticket.status, (map.get(ticket.status) ?? 0) + 1);
        }
        return Object.values(TicketStatus).map((status) => ({
            status,
            count: map.get(status) ?? 0,
        }));
    }, [searchFilteredTickets]);

    const filteredTickets = useMemo(() => {
        if (!statusFilter) return searchFilteredTickets;
        return searchFilteredTickets.filter((ticket) => ticket.status === statusFilter);
    }, [searchFilteredTickets, statusFilter]);

    const handleCloseModal = () => {
        if (modalObject.loading || modalObject.disabled) return;
        onResetModalObj();
    };

    const handleViewTicketDocument = useCallback(
        async (ticket: TicketInterface) => {
            if (!activeApp?.id) return;
            try {
                const resp = await api.documentResource.getDocumentPreviewUrl(
                    activeApp.id.toString(),
                    Number(ticket.document_id)
                );
                setPreviewFile({
                    fileUrl: resp.data.url,
                    fileName: ticket.document_name || translate('document'),
                });
            } catch (error) {
                dispatch(
                    setAlert({
                        message: getErrorMessage(error, translate('somethingWentWrong')),
                        type: 'error',
                    })
                );
            }
        },
        [activeApp?.id, dispatch, translate]
    );

    const handleAction = (action: ACTIONS, data?: TicketInterface) => {
        switch (action) {
            case ACTIONS.CREATE:
                setModalObject((prev) => ({
                    ...prev,
                    action,
                    title: translate('createTicket'),
                    data: { ...ticketInitialValues, document_id: data?.id } as TicketInterface,
                    disabled: false,
                    loading: false,
                }));
                break;
            case ACTIONS.EDIT:
            case ACTIONS.VIEW:
                if (!data) return;
                setModalObject((prev) => ({
                    ...prev,
                    action,
                    title: translate(action === ACTIONS.EDIT ? 'editTicket' : 'viewTicket'),
                    data,
                    disabled: false,
                    loading: false,
                }));
                break;
            case ACTIONS.PREVIEW_FILE:
                if (data) void handleViewTicketDocument(data);
                break;
            case ACTIONS.CLOSE:
                if (!data) return;
                setModalObject((prev) => ({
                    ...prev,
                    title: translate('closeTicket'),
                    content: translate('closeTicketConfirm').replace('{{id}}', String(data.id)),
                    data,
                    action,
                    disabled: false,
                    loading: false,
                }));
                break;
            case ACTIONS.CHAT: {
                if (!data) return;
                const references: ChatReference[] = [
                    { type: 'ticket', id: Number(data.id), name: data.text },
                ];
                navigate(`/dashboard/${id}/client-profile/chat`, {
                    state: { references },
                });
                break;
            }
            default:
                break;
        }
    };

    const handleSubmit = async (data: TicketInterface) => {
        setModalObject((prev) => ({ ...prev, disabled: true, loading: true }));
        try {
            if (data.id) {
                await api.tickets.editTicket(data);
                dispatch(setAlert({ message: translate('ticketUpdated'), type: 'success' }));
            } else {
                await api.tickets.createTicket(data);
                dispatch(setAlert({ message: translate('ticketCreated'), type: 'success' }));
            }
            await fetchTickets();
            onResetModalObj();
        } catch (error) {
            setModalObject((prev) => ({ ...prev, disabled: false, loading: false }));
            dispatch(
                setAlert({
                    message: getErrorMessage(error, translate('ticketSaveError')),
                    type: 'error',
                })
            );
        }
    };

    const handleCloseTicket = async () => {
        const ticket = modalObject.data;
        if (!ticket) return;

        setModalObject((prev) => ({ ...prev, disabled: true, loading: true }));
        try {
            await api.tickets.editTicket({ id: Number(ticket.id), status: TicketStatus.CLOSED });
            dispatch(setAlert({ message: translate('ticketClosed'), type: 'success' }));
            await fetchTickets();
            onResetModalObj();
        } catch (error) {
            setModalObject((prev) => ({ ...prev, disabled: false, loading: false }));
            dispatch(
                setAlert({
                    message: getErrorMessage(error, translate('somethingWentWrong')),
                    type: 'error',
                })
            );
        }
    };

    const handleChangeStatus = async (ticket: TicketInterface, newStatus: TicketStatus) => {
        try {
            await api.tickets.editTicket({
                id: ticket.id,
                status: newStatus,
            } as TicketInterface);
            dispatch(setAlert({ message: translate('ticketStatusUpdated'), type: 'success' }));
            await fetchTickets();
        } catch (error) {
            dispatch(
                setAlert({
                    message: getErrorMessage(error, translate('somethingWentWrong')),
                    type: 'error',
                })
            );
        }
    };

    const isFormOpen = modalObject.action === ACTIONS.CREATE || modalObject.action === ACTIONS.EDIT;

    return (
        <ThemeProvider theme={appThemeV2}>
            <Box sx={{ width: '100%', px: { xs: 2, md: 0 } }}>
                <Stack spacing={2}>
                    <TicketToolbar
                        search={search}
                        onSearchChange={setSearch}
                        statusFilter={statusFilter}
                        onStatusFilterChange={setStatusFilter}
                        statusCounts={statusCounts}
                        onCreateTicket={() => handleAction(ACTIONS.CREATE)}
                    />
                    {filteredTickets.length || loading ? (
                        <TicketTable
                            tickets={filteredTickets}
                            loading={loading}
                            handleAction={handleAction}
                            onChangeStatus={handleChangeStatus}
                        />
                    ) : tickets.length === 0 ? (
                        <Result
                            icon={<ConfirmationNumberOutlined sx={{ fontSize: 32 }} />}
                            title={translate('noTicketsYet')}
                            description={translate('noTicketsYetDescription')}
                        />
                    ) : (
                        <Result
                            icon={<SearchOff sx={{ fontSize: 32 }} />}
                            title={translate('noMatchingTickets')}
                            description={translate('noMatchingTicketsDescription')}
                        />
                    )}
                </Stack>
            </Box>

            <DocumentPreview
                open={Boolean(previewFile)}
                mobile={false}
                onClose={() => setPreviewFile(null)}
                title={translate('documentPreview')}
                fileUrl={previewFile?.fileUrl}
                fileName={previewFile?.fileName}
            />

            {isFormOpen ? (
                <TicketForm
                    isEdit={modalObject.action === ACTIONS.EDIT}
                    documents={docsList}
                    values={modalObject.data as TicketInterface}
                    disabled={modalObject.disabled || modalObject.loading}
                    onClose={handleCloseModal}
                    onSubmit={handleSubmit}
                    existingTickets={tickets}
                    onEditExistingTicket={(value) => handleAction(ACTIONS.EDIT, value)}
                    handleAction={(_action, ticket) =>
                        handleAction(ACTIONS.PREVIEW_FILE, ticket as TicketInterface)
                    }
                />
            ) : null}

            {modalObject.action === ACTIONS.VIEW && modalObject.data ? (
                <ViewTicket
                    ticket={modalObject.data}
                    onClose={handleCloseModal}
                    handleAction={handleAction}
                />
            ) : null}

            {modalObject.action === ACTIONS.CLOSE ? (
                <ConfirmationDialog
                    title={modalObject.title}
                    content={modalObject.content}
                    onClose={handleCloseModal}
                    onConfirm={handleCloseTicket}
                    loading={modalObject.loading || modalObject.disabled}
                />
            ) : null}
        </ThemeProvider>
    );
};

export default Tickets;
